import { ref, watch, onMounted, onBeforeUnmount } from 'vue';
const DEFAULT_CACHE_TTL = 15 * 60 * 1000; // 15 Minuten
export function useOpeningHours(options) {
    const { apiClient, target, cacheKey = `opening-hours-${apiClient.getProjectIdentifier?.() ?? 'default'}`, cacheTtlMs = DEFAULT_CACHE_TTL, rootMargin = '0px 0px -10% 0px', threshold = 0.15, once = true, immediate = false } = options;
    const isLoading = ref(false);
    const error = ref(null);
    const hoursData = ref(null);
    const hasFetched = ref(false);
    const observer = ref(null);
    const loadFromCache = () => {
        if (typeof window === 'undefined')
            return null;
        try {
            const raw = window.sessionStorage.getItem(cacheKey);
            if (!raw)
                return null;
            const cached = JSON.parse(raw);
            if (Date.now() - cached.timestamp > cacheTtlMs) {
                window.sessionStorage.removeItem(cacheKey);
                return null;
            }
            return cached.payload;
        }
        catch (cacheError) {
            console.warn('[useOpeningHours] Failed to read cache entry', cacheError);
            return null;
        }
    };
    const saveToCache = (payload) => {
        if (typeof window === 'undefined')
            return;
        try {
            const cached = {
                timestamp: Date.now(),
                payload
            };
            window.sessionStorage.setItem(cacheKey, JSON.stringify(cached));
        }
        catch (cacheError) {
            console.warn('[useOpeningHours] Failed to persist cache entry', cacheError);
        }
    };
    const cleanupObserver = () => {
        if (observer.value) {
            observer.value.disconnect();
            observer.value = null;
        }
    };
    const fetchOpeningHours = async (force = false) => {
        if (isLoading.value || (hasFetched.value && !force)) {
            return;
        }
        if (!force) {
            const cached = loadFromCache();
            if (cached) {
                hoursData.value = cached;
                hasFetched.value = true;
                return;
            }
        }
        if (force && typeof window !== 'undefined') {
            window.sessionStorage.removeItem(cacheKey);
        }
        try {
            isLoading.value = true;
            error.value = null;
            const response = await apiClient.getOpeningHours();
            if (response.status === 'success' && response.data) {
                hoursData.value = response.data;
                saveToCache(response.data);
                hasFetched.value = true;
            }
            else {
                error.value = response.message || 'Öffnungszeiten konnten nicht geladen werden.';
            }
        }
        catch (fetchError) {
            error.value = fetchError.message || 'Öffnungszeiten konnten nicht geladen werden.';
        }
        finally {
            isLoading.value = false;
        }
    };
    const handleIntersect = (entries, obs) => {
        for (const entry of entries) {
            if (entry.isIntersecting) {
                fetchOpeningHours();
                if (once) {
                    obs.disconnect();
                    observer.value = null;
                }
                break;
            }
        }
    };
    const initObserver = () => {
        cleanupObserver();
        if (!target.value) {
            return;
        }
        observer.value = new IntersectionObserver(handleIntersect, {
            rootMargin,
            threshold
        });
        observer.value.observe(target.value);
    };
    watch(() => target.value, (el) => {
        if (!el || hasFetched.value)
            return;
        initObserver();
    }, { immediate: true });
    onMounted(() => {
        const cached = loadFromCache();
        if (cached) {
            hoursData.value = cached;
            // Use cached data for instant paint, then revalidate immediately
            // to avoid stale "geschlossen" states after recent admin changes.
            hasFetched.value = false;
            fetchOpeningHours(true);
            return;
        }
        if (immediate) {
            fetchOpeningHours();
            return;
        }
        if (target.value) {
            initObserver();
            return;
        }
    });
    onBeforeUnmount(() => {
        cleanupObserver();
    });
    return {
        isLoading,
        error,
        data: hoursData,
        hasFetched,
        refetch: () => fetchOpeningHours(true)
    };
}
